import { createContext, type ReactNode, useLayoutEffect, useMemo, useRef } from 'react';

import { analytics, IMPERSONATION_FLAG_KEYS, resolveImpersonationSessionFlags } from '@/analytics';
import { holdPageviews, releasePageviews } from '@/analytics/core/pageviewGate';
import { useGetSession } from '@/domain/auth';
import { apiErrorStatus } from '@/domain/shared/errors';
import { getEnvironmentByHostname } from '@/utils/environmentByHostname';
import { setHotjarUserFromMe } from '@/utils/initializeAnalyticsTools';

import { AUDIT_LOG_READ_PERMISSION } from './permissions';
import { isClientAdminRole } from './roleHelpers';
import type { AuthClient, AuthContextValue, AuthUser, HasUserFeatureOptions } from './types';

const DEFAULT_AUTH_CONTEXT_VALUE: AuthContextValue = {
    isAuthenticated: false,
    isAuthenticating: true,
    error: null,
    sessionUnavailable: false,
    retrySession: () => undefined,
    isRetryingSession: false,
    user: null,
    clients: [],
    isOptoScaleAdmin: false,
    canViewAuditLog: false,
    isClientAdmin: () => false,
    hasFullClientAccess: () => false,
    isLocationAdmin: () => false,
    canAdministerLocation: () => false,
    hasUserFeature: () => false,
    canMoveBioscopes: () => false,
};

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
    children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
    const appLoadedRef = useRef(false);
    const identityPhaseRef = useRef<'unknown' | 'authenticated' | 'anonymous'>('unknown');
    const { isLoading, isFetching, error, session, refetch } = useGetSession();

    const value = useMemo<AuthContextValue>(() => {
        const user: AuthUser | null = session?.user ?? null;
        const clients: AuthClient[] = session?.clients ?? [];

        const isOptoScaleAdmin = user?.systemRole === 'ADMIN';

        // Mirrors the backend guard, including refusing impersonated sessions.
        const canViewAuditLog =
            isOptoScaleAdmin &&
            !user?.impersonatorUser &&
            (user?.permissions ?? []).includes(AUDIT_LOG_READ_PERMISSION);

        const isValidId = (id: number): boolean => Number.isFinite(id) && id > 0;

        const hasClientAdminRole = (clientId: number): boolean =>
            isValidId(clientId) &&
            clients.some((client) => client.id === clientId && isClientAdminRole(client.role));

        const isClientAdmin = (clientId: number): boolean => {
            if (isOptoScaleAdmin) return true;
            return hasClientAdminRole(clientId);
        };

        const hasFullClientAccess = (clientId: number): boolean => {
            if (isOptoScaleAdmin) return true;
            return hasClientAdminRole(clientId);
        };

        const isLocationAdmin = (clientId: number): boolean => {
            if (hasFullClientAccess(clientId)) return true;
            if (!isValidId(clientId)) return false;
            return clients.some(
                (client) => client.id === clientId && client.role === 'LOCATION_ADMIN'
            );
        };

        const canAdministerLocation = (clientId: number, locationId: number): boolean => {
            if (!isValidId(clientId) || !isValidId(locationId)) return false;
            if (hasFullClientAccess(clientId)) return true;
            return clients.some(
                (client) =>
                    client.id === clientId &&
                    client.role === 'LOCATION_ADMIN' &&
                    client.locationIds.includes(locationId)
            );
        };

        const hasUserFeature = (
            flags: string | string[],
            opts?: HasUserFeatureOptions
        ): boolean => {
            // OptoScale admins pass all feature checks unless noSuper is set
            if (isOptoScaleAdmin && !opts?.noSuper) return true;

            const flagArray = Array.isArray(flags) ? flags : [flags];
            return flagArray.every((flag) => (user?.featureFlags?.indexOf(flag) ?? -1) >= 0);
        };

        const canMoveBioscopes = (clientId: number, locationId?: number): boolean => {
            if (hasFullClientAccess(clientId)) return true;
            if (locationId == null) return false;
            return canAdministerLocation(clientId, locationId);
        };

        // Only a 401 says nobody is signed in. Any other failure leaves it unknown, and the
        // user who is still signed in gets a retry instead of the login page.
        const sessionUnavailable =
            !isLoading && user === null && error !== null && apiErrorStatus(error) !== 401;

        return {
            isAuthenticated: !!user,
            isAuthenticating: isLoading,
            error,
            sessionUnavailable,
            retrySession: () => {
                void refetch();
            },
            isRetryingSession: isFetching,
            user,
            clients,
            isOptoScaleAdmin,
            canViewAuditLog,
            isClientAdmin,
            hasFullClientAccess,
            isLocationAdmin,
            canAdministerLocation,
            hasUserFeature,
            canMoveBioscopes,
        };
    }, [session, error, isLoading, isFetching, refetch]);

    // Layout, not passive: this runs before AnalyticsProvider's pageview effect, so the
    // first pageview is held until identify or reset has settled the distinct id.
    useLayoutEffect(() => {
        if (value.isAuthenticating) {
            holdPageviews();
            return;
        }

        if (value.isAuthenticated) {
            setHotjarUserFromMe({
                isOptoScaleAdmin: value.isOptoScaleAdmin,
                state: {
                    user: value.user,
                    clients: value.clients,
                },
            });

            if (value.user?.id) {
                const primaryClient = value.clients[0];

                analytics.identifyUser({
                    userId: String(value.user.id),
                    email: value.user.email,
                    name:
                        [value.user.firstName, value.user.lastName].filter(Boolean).join(' ') ||
                        value.user.email,
                    environment: getEnvironmentByHostname(),
                    isOptoScaleAdmin: value.isOptoScaleAdmin,
                    isClientAdmin: value.clients.some((c) => isClientAdminRole(c.role)),
                    isLocationAdmin: value.clients.some((c) => c.role === 'LOCATION_ADMIN'),
                    clientId: primaryClient?.id ?? null,
                    clientName: primaryClient?.name ?? null,
                    client: primaryClient ? `${primaryClient.id} (${primaryClient.name})` : null,
                    ...(!value.isOptoScaleAdmin && {
                        licenses: [...new Set(value.clients.flatMap((c) => c.authLicenses))],
                    }),
                    isOptoScaleAdminGroup: value.isOptoScaleAdmin,
                    primaryClient: primaryClient
                        ? {
                              id: primaryClient.id,
                              name: primaryClient.name,
                              licenses: primaryClient.authLicenses,
                              locationCount: primaryClient.locations.length,
                              locationNames: primaryClient.locations.map((l) => l.name),
                          }
                        : undefined,
                });

                const impersonationFlags = resolveImpersonationSessionFlags(
                    value.user.impersonatorUser
                );
                if (impersonationFlags) {
                    analytics.setSessionFlags(impersonationFlags);
                } else {
                    analytics.clearSessionFlags([...IMPERSONATION_FLAG_KEYS]);
                }

                if (!appLoadedRef.current) {
                    appLoadedRef.current = true;
                    analytics.appLoaded({
                        userRole: value.isOptoScaleAdmin
                            ? 'optoscale_admin'
                            : (primaryClient?.role ?? 'user'),
                    });
                }

                identityPhaseRef.current = 'authenticated';
            }

            releasePageviews();
            return;
        }

        appLoadedRef.current = false;
        if (identityPhaseRef.current !== 'anonymous') {
            holdPageviews();
            identityPhaseRef.current = 'anonymous';
            analytics.reset();
        }
        releasePageviews();
    }, [
        value.isAuthenticating,
        value.isAuthenticated,
        value.isOptoScaleAdmin,
        value.user,
        value.clients,
    ]);

    return <AuthContext value={value}>{children}</AuthContext>;
}

export { DEFAULT_AUTH_CONTEXT_VALUE };
